import { http } from "./api";
import { toast } from "sonner";
import i18n from "../i18n";

const t = (key, options) => i18n.t(key, options);

/**
 * Service API pour la gestion complète du planning
 * Inclut: Cycle Patterns, Schedule Overrides, Planning, Attendance Calculation, History, Notifications
 */

// =====================================
// CYCLE PATTERNS - Gestion des cycles de rotation
// =====================================

export const cyclePatternAPI = {
  /**
   * Récupérer la liste des patterns de cycle
   */
  async getCyclePatterns(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.skip != null) params.append("skip", filters.skip);
      if (filters.limit != null) params.append("limit", filters.limit);
      if (filters.is_active != null) params.append("is_active", filters.is_active);

      const response = await http.get(`v1/cycle-patterns/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Erreur récupération cycle patterns:", error);
      throw error;
    }
  },

  /**
   * Récupérer un cycle pattern par ID
   */
  async getCyclePattern(patternId) {
    try {
      const response = await http.get(`v1/cycle-patterns/${patternId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur récupération cycle pattern ${patternId}:`, error);
      throw error;
    }
  },

  /**
   * Créer un nouveau cycle pattern
   */
  async createCyclePattern(patternData) {
    try {
      const response = await http.post("v1/cycle-patterns/", patternData);

      toast.success(t("planning.service.cyclePatterns.created"), {
        description: t("planning.service.cyclePatterns.createdDesc", { name: patternData.name }),
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error("Erreur création cycle pattern:", error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.cyclePatterns.createError"),
      });
      throw error;
    }
  },

  /**
   * Mettre à jour un cycle pattern
   */
  async updateCyclePattern(patternId, patternData) {
    try {
      const response = await http.put(`v1/cycle-patterns/${patternId}`, patternData);

      toast.success(t("planning.service.cyclePatterns.updated"), {
        description: t("planning.service.cyclePatterns.updatedDesc"),
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error(`Erreur mise à jour cycle pattern ${patternId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.cyclePatterns.updateError"),
      });
      throw error;
    }
  },

  /**
   * Supprimer un cycle pattern
   */
  async deleteCyclePattern(patternId) {
    try {
      await http.delete(`v1/cycle-patterns/${patternId}`);

      toast.success(t("planning.service.cyclePatterns.deleted"), {
        description: t("planning.service.cyclePatterns.deletedDesc"),
        duration: 3000,
      });
    } catch (error) {
      console.error(`Erreur suppression cycle pattern ${patternId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.cyclePatterns.deleteError"),
      });
      throw error;
    }
  },

  /**
   * Ajouter une semaine à un cycle pattern
   */
  async addCycleWeek(patternId, weekData) {
    try {
      const response = await http.post(`v1/cycle-patterns/${patternId}/weeks`, weekData);

      toast.success(t("planning.service.cyclePatterns.weekAdded"), {
        description: t("planning.service.cyclePatterns.weekAddedDesc", { week: weekData.week_number }),
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error(`Erreur ajout semaine au cycle ${patternId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.cyclePatterns.addWeekError"),
      });
      throw error;
    }
  },

  /**
   * Mettre à jour une semaine de cycle
   */
  async updateCycleWeek(weekId, weekData) {
    try {
      const response = await http.put(`v1/cycle-patterns/weeks/${weekId}`, weekData);

      toast.success(t("planning.service.cyclePatterns.weekUpdated"), {
        description: t("planning.service.cyclePatterns.weekUpdatedDesc"),
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error(`Erreur mise à jour semaine ${weekId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.cyclePatterns.updateWeekError"),
      });
      throw error;
    }
  },

  /**
   * Supprimer une semaine de cycle
   */
  async deleteCycleWeek(weekId) {
    try {
      await http.delete(`v1/cycle-patterns/weeks/${weekId}`);

      toast.success(t("planning.service.cyclePatterns.weekDeleted"), {
        description: t("planning.service.cyclePatterns.weekDeletedDesc"),
        duration: 3000,
      });
    } catch (error) {
      console.error(`Erreur suppression semaine ${weekId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.cyclePatterns.deleteWeekError"),
      });
      throw error;
    }
  },
};

// =====================================
// SCHEDULE OVERRIDES - Changements exceptionnels avec workflow d'approbation
// =====================================

export const scheduleOverrideAPI = {
  /**
   * Récupérer la liste des overrides
   */
  async getScheduleOverrides(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.skip != null) params.append("skip", filters.skip);
      if (filters.limit != null) params.append("limit", filters.limit);
      if (filters.employee_id) params.append("employee_id", filters.employee_id);
      if (filters.status) params.append("status", filters.status);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);

      const response = await http.get(`v1/schedule-overrides/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Erreur récupération overrides:", error);
      throw error;
    }
  },

  /**
   * Récupérer le résumé des overrides
   */
  async getOverridesSummary(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.employee_id) params.append("employee_id", filters.employee_id);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);

      const response = await http.get(`v1/schedule-overrides/summary?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Erreur récupération résumé overrides:", error);
      throw error;
    }
  },

  /**
   * Récupérer un override par ID
   */
  async getScheduleOverride(overrideId) {
    try {
      const response = await http.get(`v1/schedule-overrides/${overrideId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur récupération override ${overrideId}:`, error);
      throw error;
    }
  },

  /**
   * Créer une demande de changement exceptionnel
   */
  async createScheduleOverride(overrideData) {
    try {
      const response = await http.post("v1/schedule-overrides/", overrideData);

      toast.success(t("planning.service.overrides.created"), {
        description: t("planning.service.overrides.createdDesc"),
        duration: 4000,
      });

      return response.data;
    } catch (error) {
      console.error("Erreur création override:", error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.overrides.createError"),
      });
      throw error;
    }
  },

  /**
   * Mettre à jour un override (seulement si PENDING)
   */
  async updateScheduleOverride(overrideId, overrideData) {
    try {
      const response = await http.put(`v1/schedule-overrides/${overrideId}`, overrideData);

      toast.success(t("planning.service.overrides.updated"), {
        description: t("planning.service.overrides.updatedDesc"),
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error(`Erreur mise à jour override ${overrideId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.overrides.updateError"),
      });
      throw error;
    }
  },

  /**
   * Approuver ou rejeter un override (manager)
   */
  async approveOrRejectOverride(overrideId, approvalData) {
    try {
      const response = await http.post(`v1/schedule-overrides/${overrideId}/approve`, approvalData);

      if (approvalData.approved) {
        toast.success(t("planning.service.overrides.approved"), {
          description: t("planning.service.overrides.approvedDesc"),
          duration: 3000,
        });
      } else {
        toast.success(t("planning.service.overrides.rejected"), {
          description: t("planning.service.overrides.rejectedDesc"),
          duration: 3000,
        });
      }

      return response.data;
    } catch (error) {
      console.error(`Erreur approbation override ${overrideId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.overrides.processError"),
      });
      throw error;
    }
  },

  /**
   * Supprimer un override
   */
  async deleteScheduleOverride(overrideId) {
    try {
      await http.delete(`v1/schedule-overrides/${overrideId}`);

      toast.success(t("planning.service.overrides.deleted"), {
        description: t("planning.service.overrides.deletedDesc"),
        duration: 3000,
      });
    } catch (error) {
      console.error(`Erreur suppression override ${overrideId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.overrides.deleteError"),
      });
      throw error;
    }
  },
};

// =====================================
// PLANNING - Calculs et prévisualisation
// =====================================

export const planningCalculationAPI = {
  /**
   * Récupérer le planning effectif d'un employé sur une période
   */
  async getEmployeeSchedule(employeeId, startDate, endDate) {
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });

      const response = await http.get(`v1/planning/employees/${employeeId}/schedule?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur récupération planning employé ${employeeId}:`, error);
      throw error;
    }
  },

  /**
   * Récupérer le planning d'aujourd'hui pour un employé
   */
  async getEmployeeScheduleToday(employeeId) {
    try {
      const response = await http.get(`v1/planning/employees/${employeeId}/schedule/today`);
      return response.data;
    } catch (error) {
      console.error(`Erreur récupération planning aujourd'hui pour employé ${employeeId}:`, error);
      throw error;
    }
  },

  /**
   * Prévisualiser le planning d'un employé sur X semaines
   */
  async previewEmployeeSchedule(employeeId, previewData) {
    try {
      const response = await http.post(`v1/planning/employees/${employeeId}/schedule/preview`, previewData);
      return response.data;
    } catch (error) {
      console.error(`Erreur prévisualisation planning employé ${employeeId}:`, error);
      throw error;
    }
  },

  /**
   * Calculer l'état de présence avec tolérances et couleurs
   */
  async calculateAttendanceForDate(employeeId, date, actualStart = null, actualEnd = null) {
    try {
      const params = new URLSearchParams({ date });
      if (actualStart) params.append("actual_start", actualStart);
      if (actualEnd) params.append("actual_end", actualEnd);

      const response = await http.get(
        `/v1/planning/attendance/calculate/${employeeId}/${date}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur calcul présence pour employé ${employeeId} le ${date}:`, error);
      throw error;
    }
  },

  /**
   * Calcul en masse des présences sur une période
   */
  async bulkCalculateAttendances(calculationData) {
    try {
      const response = await http.post("v1/planning/attendance/calculate/bulk", calculationData);

      toast.success(t("planning.service.calculations.completed"), {
        description: t("planning.service.calculations.completedDesc", {
          count: response.data.total_updated,
          seconds: response.data.duration_seconds,
        }),
        duration: 5000,
      });

      return response.data;
    } catch (error) {
      console.error("Erreur calcul en masse présences:", error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.calculations.calculateError"),
      });
      throw error;
    }
  },

  /**
   * Récupérer l'historique des changements de planning
   */
  async getScheduleHistory(employeeId, filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.skip != null) params.append("skip", filters.skip);
      if (filters.limit != null) params.append("limit", filters.limit);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);

      const response = await http.get(`v1/planning/schedule/history/${employeeId}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur récupération historique planning employé ${employeeId}:`, error);
      throw error;
    }
  },
};

// =====================================
// NOTIFICATIONS - Gestion des notifications
// =====================================

export const notificationAPI = {
  /**
   * Récupérer les notifications de l'utilisateur
   */
  async getUserNotifications(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.skip != null) params.append("skip", filters.skip);
      if (filters.limit != null) params.append("limit", filters.limit);
      if (filters.unread_only != null) params.append("unread_only", filters.unread_only);
      if (filters.notification_type) params.append("notification_type", filters.notification_type);
      if (filters.priority) params.append("priority", filters.priority);

      const response = await http.get(`v1/notifications/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Erreur récupération notifications:", error);
      throw error;
    }
  },

  /**
   * Récupérer le résumé des notifications
   */
  async getNotificationsSummary() {
    try {
      const response = await http.get("v1/notifications/summary");
      return response.data;
    } catch (error) {
      console.error("Erreur récupération résumé notifications:", error);
      throw error;
    }
  },

  /**
   * Marquer une notification comme lue
   */
  async markNotificationAsRead(notificationId) {
    try {
      const response = await http.put(`v1/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error(`Erreur marquage notification ${notificationId} comme lue:`, error);
      throw error;
    }
  },

  /**
   * Marquer plusieurs notifications comme lues
   */
  async markMultipleAsRead(notificationIds) {
    try {
      const response = await http.put("v1/notifications/mark-read-bulk", {
        notification_ids: notificationIds,
      });

      toast.success(t("planning.service.notifications.markedRead"), {
        description: t("planning.service.notifications.markedReadDesc", { count: notificationIds.length }),
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error("Erreur marquage multiple notifications:", error);
      toast.error(t("common.error"), {
        description: t("planning.service.notifications.markReadError"),
      });
      throw error;
    }
  },

  /**
   * Marquer toutes les notifications comme lues
   */
  async markAllAsRead() {
    try {
      const response = await http.put("v1/notifications/mark-all-read");

      toast.success(t("planning.service.notifications.allMarkedRead"), {
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error("Erreur marquage toutes notifications:", error);
      toast.error(t("common.error"), {
        description: t("planning.service.notifications.markAllReadError"),
      });
      throw error;
    }
  },

  /**
   * Supprimer une notification
   */
  async deleteNotification(notificationId) {
    try {
      await http.delete(`v1/notifications/${notificationId}`);

      toast.success(t("planning.service.notifications.deleted"), {
        duration: 2000,
      });
    } catch (error) {
      console.error(`Erreur suppression notification ${notificationId}:`, error);
      toast.error(t("common.error"), {
        description: t("planning.service.notifications.deleteError"),
      });
      throw error;
    }
  },

  /**
   * Supprimer toutes les notifications lues
   */
  async deleteAllRead() {
    try {
      const response = await http.delete("v1/notifications/delete-all-read");

      toast.success(t("planning.service.notifications.readDeleted"), {
        description: t("planning.service.notifications.readDeletedDesc", { count: response.data.deleted_count }),
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error("Erreur suppression notifications lues:", error);
      toast.error(t("common.error"), {
        description: t("planning.service.notifications.deleteReadError"),
      });
      throw error;
    }
  },
};

// =====================================
// SCHEDULES - API pour récupérer les schedules (utilisé par les composants)
// =====================================

export const schedulesAPI = {
  /**
   * Récupérer la liste de tous les schedules disponibles
   */
  async getSchedules(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.skip != null) params.append("skip", filters.skip);
      if (filters.limit != null) params.append("limit", filters.limit);
      if (filters.is_active != null) params.append("is_active", filters.is_active);

      const response = await http.get(`v1/schedules/?${params.toString()}`);
      const data = response.data;

      // Normaliser la réponse pour que les composants puissent utiliser .items
      if (Array.isArray(data)) {
        return {
          items: data,
          total: data.length,
        };
      }

      return data;
    } catch (error) {
      console.error("Erreur récupération schedules:", error);
      throw error;
    }
  },

  /**
   * Récupérer un schedule par ID
   */
  async getSchedule(scheduleId) {
    try {
      const response = await http.get(`v1/schedules/${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur récupération schedule ${scheduleId}:`, error);
      throw error;
    }
  },

  /**
   * Créer un planning
   */
  async createSchedule(scheduleData) {
    try {
      const response = await http.post("v1/schedules/", scheduleData);
      toast.success(t("planning.service.schedules.created"), {
        description: scheduleData.name,
      });
      return response.data;
    } catch (error) {
      console.error("Erreur création planning:", error);
      toast.error(t("planning.service.schedules.createError"), {
        description: error.response?.data?.detail,
      });
      throw error;
    }
  },

  /**
   * Mettre à jour un planning
   */
  async updateSchedule(scheduleId, scheduleData) {
    try {
      const response = await http.put(`v1/schedules/${scheduleId}`, scheduleData);
      toast.success(t("planning.service.schedules.updated"));
      return response.data;
    } catch (error) {
      console.error(`Erreur mise à jour planning ${scheduleId}:`, error);
      toast.error(t("planning.service.schedules.updateError"), {
        description: error.response?.data?.detail,
      });
      throw error;
    }
  },

  /**
   * Supprimer un planning
   * @param {number} scheduleId
   * @param {boolean} force - si true, supprime aussi les affectations liées
   */
  async deleteSchedule(scheduleId, force = false) {
    try {
      const url = force
        ? `v1/schedules/${scheduleId}?force=true`
        : `v1/schedules/${scheduleId}`;
      await http.delete(url);
      toast.success(t("planning.service.schedules.deleted"));
    } catch (error) {
      console.error(`Erreur suppression planning ${scheduleId}:`, error);
      if (!force) {
        // Don't toast here — let the caller handle the 400 to offer force-delete
        throw error;
      }
      toast.error(t("planning.service.schedules.deleteError"), {
        description: error.response?.data?.detail,
      });
      throw error;
    }
  },

  /**
   * Ajouter un créneau horaire au planning
   */
  async addTimeSlot(scheduleId, timeSlotData) {
    try {
      const response = await http.post(`v1/schedules/${scheduleId}/time_slots`, timeSlotData);
      toast.success(t("planning.service.schedules.slotAdded"));
      return response.data;
    } catch (error) {
      console.error(`Erreur ajout créneau planning ${scheduleId}:`, error);
      toast.error(t("planning.service.schedules.addSlotError"), {
        description: error.response?.data?.detail,
      });
      throw error;
    }
  },

  /**
   * Mettre à jour un créneau
   */
  async updateTimeSlot(timeSlotId, timeSlotData) {
    try {
      const response = await http.put(`v1/schedules/time_slots/${timeSlotId}`, timeSlotData);
      toast.success(t("planning.service.schedules.slotUpdated"));
      return response.data;
    } catch (error) {
      console.error(`Erreur mise à jour créneau ${timeSlotId}:`, error);
      toast.error(t("planning.service.schedules.updateSlotError"), {
        description: error.response?.data?.detail,
      });
      throw error;
    }
  },

  /**
   * Supprimer un créneau
   */
  async deleteTimeSlot(timeSlotId) {
    try {
      await http.delete(`v1/schedules/time_slots/${timeSlotId}`);
      toast.success(t("planning.service.schedules.slotDeleted"));
    } catch (error) {
      console.error(`Erreur suppression créneau ${timeSlotId}:`, error);
      toast.error(t("planning.service.schedules.deleteSlotError"), {
        description: error.response?.data?.detail,
      });
      throw error;
    }
  },
};

// =====================================
// SCHEDULE ASSIGNMENTS - Affectations de planning aux employés
// =====================================

export const scheduleAssignmentAPI = {
  /**
   * Récupérer les affectations de planning
   */
  async getScheduleAssignments(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.employee_id) params.append("employee_id", filters.employee_id);
      if (filters.schedule_id) params.append("schedule_id", filters.schedule_id);
      if (filters.cycle_pattern_id) params.append("cycle_pattern_id", filters.cycle_pattern_id);
      if (filters.is_active != null) params.append("is_active", filters.is_active);

      const response = await http.get(`v1/schedule-assignments/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Erreur récupération affectations planning:", error);
      throw error;
    }
  },

  /**
   * Créer une affectation de planning
   */
  async createScheduleAssignment(assignmentData) {
    try {
      const response = await http.post("v1/schedule-assignments/", assignmentData);

      toast.success(t("planning.service.assignments.created"), {
        description: t("planning.service.assignments.createdDesc"),
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Mettre à jour une affectation de planning
   */
  async updateScheduleAssignment(assignmentId, assignmentData) {
    try {
      const response = await http.put(`v1/schedule-assignments/${assignmentId}`, assignmentData);

      toast.success(t("planning.service.assignments.updated"), {
        description: t("planning.service.assignments.updatedDesc"),
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error(`Erreur mise à jour affectation ${assignmentId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.assignments.updateError"),
      });
      throw error;
    }
  },

  /**
   * Supprimer une affectation de planning
   */
  async deleteScheduleAssignment(assignmentId) {
    try {
      await http.delete(`v1/schedule-assignments/${assignmentId}`);

      toast.success(t("planning.service.assignments.deleted"), {
        description: t("planning.service.assignments.deletedDesc"),
        duration: 3000,
      });
    } catch (error) {
      console.error(`Erreur suppression affectation ${assignmentId}:`, error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.assignments.deleteError"),
      });
      throw error;
    }
  },

  /**
   * Créer des affectations en masse
   */
  async bulkCreateScheduleAssignments(bulkData) {
    try {
      const response = await http.post("v1/schedule-assignments/bulk", bulkData);

      const result = response.data;

      toast.success(t("planning.service.assignments.bulkCreated"), {
        description: t("planning.service.assignments.bulkCreatedDesc", {
          created: result.total_created,
          requested: result.total_requested,
          skippedText: result.total_skipped > 0 ? t("planning.service.assignments.bulkSkipped", { count: result.total_skipped }) : "",
        }),
        duration: 4000,
      });

      return result;
    } catch (error) {
      console.error("Erreur création affectations en masse:", error);
      toast.error(t("common.error"), {
        description: error.response?.data?.detail || t("planning.service.assignments.bulkCreateError"),
      });
      throw error;
    }
  },
};

// Export combiné pour faciliter l'utilisation
export const planningAPI = {
  cyclePatterns: cyclePatternAPI,
  overrides: scheduleOverrideAPI,
  calculations: planningCalculationAPI,
  notifications: notificationAPI,
  schedules: schedulesAPI,
  assignments: scheduleAssignmentAPI,
};

export default planningAPI;
